import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCameraFov } from 'store/actions';
import './style.scss';
// Logo will be referenced from public directory

const levelSceneMapping = {
  1: { id: 'basement-47b0cf97', groupId: 'basement' },
  2: { id: 'stair-1st-floor-a5de9f7b', groupId: '1st-floor' },
  3: { id: 'stair-2nd-floor-c736a91a', groupId: '2nd-floor' },
  4: { id: 'stair-3rd-floor-f8486246', groupId: '3rd-floor' },
  5: { id: 'stair-4th-floor-aad5b3de', groupId: '4th-floor' },
  6: { id: 'music-lounge-a50ed418', groupId: '5th-floor' },
};

const navigationTabs = [
  { id: 1, name: 'Basement', active: false },
  { id: 2, name: '1', active: true },
  { id: 3, name: '2', active: false },
  { id: 4, name: '3', active: false },
  { id: 5, name: '4', active: false },
  { id: 6, name: '5', active: false },
];

const zoomTabs = [
  { id: 1, name: '0.5x', fov: 100 },
  { id: 2, name: '1x', fov: 80 },
  { id: 3, name: '1.5x', fov: 60 },
];

function Menu({ scenes = [], currentPano, onSelect }) {
  const hasAutoNavigated = useRef(false);
  const [currentZoomLevel, setCurrentZoomLevel] = useState(2);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const { isForceMobile } = useSelector((state) => state);
  const dispatch = useDispatch();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const initialZoomTab = zoomTabs.find((tab) => tab.id === currentZoomLevel);
    if (initialZoomTab) {
      dispatch(setCameraFov(initialZoomTab.fov));
      window.panoFov = initialZoomTab.fov;
      console.log(
        `Initial zoom set to ${initialZoomTab.name} (FOV: ${initialZoomTab.fov})`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentZoomLevel]);

  useEffect(() => {
    if (currentPano) {
      const currentZoomTab = zoomTabs.find(
        (tab) => tab.id === currentZoomLevel
      );

      if (currentZoomTab) {
        dispatch(setCameraFov(currentZoomTab.fov));
        window.panoFov = currentZoomTab.fov;

        if (window.camera) {
          window.camera.fov = currentZoomTab.fov;
          window.camera.updateProjectionMatrix();
        }

        console.log(
          `Scene changed - Applied current zoom ${currentZoomTab.name} (FOV: ${currentZoomTab.fov})`
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPano, dispatch, currentZoomLevel]);

  const getLevelScene = (levelNumber) => {
    const targetMapping = levelSceneMapping[levelNumber];
    if (!targetMapping) return null;

    const scene = scenes.find(
      (s) => s.id === targetMapping.id && s.groupId === targetMapping.groupId
    );

    return scene || null;
  };

  const handleLevelClick = (level) => {
    const targetScene = getLevelScene(level);
    console.log('Target scene for level click:', targetScene, onSelect);
    if (targetScene && onSelect) {
      console.log(`Navigating to Level ${level}:`, {
        sceneId: targetScene.id,
        groupId: targetScene.groupId,
        title: targetScene.title || `Level ${level}`,
      });
      onSelect(targetScene.groupId, targetScene.id);
    } else {
      console.warn(
        `Scene not found for Level ${level}:`,
        levelSceneMapping[level]
      );
    }
  };

  const handleZoomClick = (zoomLevel) => {
    const zoomTab = zoomTabs.find((tab) => tab.id === zoomLevel);
    if (zoomTab) {
      setCurrentZoomLevel(zoomLevel);

      // Redux
      dispatch(setCameraFov(zoomTab.fov));

      window.panoFov = zoomTab.fov;
      if (window.camera) {
        window.camera.fov = zoomTab.fov;
        window.camera.updateProjectionMatrix();
      }

      console.log(`Zoom changed to ${zoomTab.name} (FOV: ${zoomTab.fov})`);
    }
  };

  const isLevelActive = (level) => {
    const targetMapping = levelSceneMapping[level];
    if (!targetMapping || !currentPano) return false;

    return currentPano.groupId === targetMapping.groupId;
  };

  const isZoomActive = (zoomLevel) => {
    return currentZoomLevel === zoomLevel;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasAutoNavigated.current && scenes.length > 0 && onSelect) {
        const activeTab = navigationTabs.find((tab) => tab.active === true);
        const targetLevel = activeTab ? activeTab.id : 2;
        const targetMapping = levelSceneMapping[targetLevel];

        if (targetMapping) {
          const targetScene = scenes.find(
            (s) =>
              s.id === targetMapping.id && s.groupId === targetMapping.groupId
          );

          if (targetScene) {
            hasAutoNavigated.current = true;
            console.log(
              `Auto-navigating to Level ${targetLevel} (${
                activeTab?.name || 'Default'
              })`
            );
            onSelect(targetScene.groupId, targetScene.id);
          }
        }
      }
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenes, onSelect]);

  return (
    <>
      <div className={`menu-container ${isForceMobile ? 'force-mobile' : ''}`}>
        <div className="navigation-tabs">
          <label>FLOOR:</label>
          {navigationTabs.map((tab) => (
            <div
              key={tab.id}
              className={`nav-tab ${isLevelActive(tab.id) ? 'active' : ''}`}
              onClick={() => handleLevelClick(tab.id)}
            >
              <span>{tab.id === 1 && isMobile ? 'Base' : tab.name}</span>
            </div>
          ))}
          <div className="vertical-line"></div>
          <div className="zoom-tabs">
            <label>ZOOM:</label>
            {zoomTabs.map((tab) => (
              <div
                key={tab.id}
                className={`nav-tab ${isZoomActive(tab.id) ? 'active' : ''}`}
                onClick={() => handleZoomClick(tab.id)}
              >
                <span>{tab.name}</span>
              </div>
            ))}
          </div>
        </div>
        {!isForceMobile && (
          <div className="left-logo">
            <img src="/TheGlen-logo.png" alt="logo" />
          </div>
        )}
      </div>
    </>
  );
}

export default Menu;
